import { API, Auth } from 'aws-amplify';
import toast from 'react-hot-toast';
import { withTimeout, NetworkError, isOffline, getCachedIdToken } from '../../services/network';
import {
  routeGet as routeEventsGet,
  routePost as routeEventsPost,
  routePut as routeEventsPut,
  routeDelete as routeEventsDelete,
} from '../../services/db/router';

const API_NAME = 'babytrackApi';
const SESSION_TIMEOUT_MS = 4000;
const REQUEST_TIMEOUT_MS = 12000;

async function getAuthHeaders() {
  try {
    const session = await withTimeout(Auth.currentSession(), SESSION_TIMEOUT_MS, 'Session lookup timed out');
    return {
      Authorization: session.getIdToken().getJwtToken(),
    };
  } catch {
    // Auth.currentSession() can hang on Capacitor WKWebView (slow refresh,
    // storage quirks) and exceed the 4 s timeout. Falling back to {} sends
    // the request anonymously, which API Gateway rejects with 401 — and the
    // 401 has no CORS headers, so the WebView surfaces it as "Network Error"
    // (see BabySetupPage rejection on build 35). Read the raw idToken from
    // localStorage instead so authenticated requests still go through.
    const cachedIdToken = getCachedIdToken();
    if (cachedIdToken) return { Authorization: cachedIdToken };
    return {};
  }
}

function classifyError(err) {
  if (err?.isTimeoutError || err?.isNetworkError) return 'network';
  if (err?.message === 'Network Error' || err?.code === 'NetworkError') return 'network';
  if (isOffline()) return 'network';
  return 'other';
}

function handleResponse(promise) {
  return promise.catch((err) => {
    if (err?.response?.status === 401 || err?.message?.includes('401')) {
      window.location.href = '/login';
      return;
    }
    if (classifyError(err) === 'network') {
      // Swallow toast for offline/timeout — callers should treat this as transient.
      // The sync engine (Phase C) will retry these automatically.
      const ne = new NetworkError(err?.message || 'Network unavailable');
      throw ne;
    }
    const message = err?.response?.data?.error || err?.message || 'Something went wrong';
    toast.error(message);
    throw err;
  });
}

function withRequestTimeout(p, timeoutMs = REQUEST_TIMEOUT_MS) {
  return withTimeout(p, timeoutMs, 'Request timed out');
}

// The device's IANA timezone, or null when Intl cannot say (very old
// WebViews, a broken locale) — never throws.
function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export async function apiGet(path, queryParams = {}) {
  const local = await routeEventsGet(path, queryParams);
  if (local !== null) return local;
  const headers = await getAuthHeaders();
  let query = queryParams;
  // GET /babies is the launch-time call every screen makes, so it doubles as
  // the place the server learns this device's timezone (persisted on the
  // user's PROFILE by babyProfileHandler; the weekly summary email buckets
  // the week in it). A query parameter, not a header: a custom header would
  // need the API Gateway CORS preflight changed on every route.
  if (path === '/babies') {
    const tz = deviceTimeZone();
    // `cv` = client version marker (150 = 1.5.0). On a brand-new account the
    // server stamps PROFILE.noPreview from it: accounts created on 1.5.0+
    // clients get the onboarding paywall instead of the 7-day preview, while
    // accounts created on older builds keep the preview (docs/v150-spec.md
    // decision 1, babyProfileHandler.listBabies).
    query = { ...queryParams, cv: 150 };
    if (tz) query.tz = tz;
  }
  return handleResponse(
    withRequestTimeout(API.get(API_NAME, path, { headers, queryStringParameters: query }))
  );
}

// `timeoutMs` lets a slow-by-design call (the Atlas chat, which API Gateway
// cuts at 29 s) outlive the 12 s default without changing it for anything else.
export async function apiPost(path, body = {}, { timeoutMs } = {}) {
  const local = await routeEventsPost(path, body);
  if (local !== null) return local;
  const headers = await getAuthHeaders();
  return handleResponse(
    withRequestTimeout(API.post(API_NAME, path, { headers, body }), timeoutMs)
  );
}

export async function apiPut(path, body = {}) {
  const local = await routeEventsPut(path, body);
  if (local !== null) return local;
  const headers = await getAuthHeaders();
  return handleResponse(
    withRequestTimeout(API.put(API_NAME, path, { headers, body }))
  );
}

export async function apiDelete(path) {
  const local = await routeEventsDelete(path);
  if (local !== null) return local;
  const headers = await getAuthHeaders();
  return handleResponse(
    withRequestTimeout(API.del(API_NAME, path, { headers }))
  );
}

// Raw fetch for export endpoints that return non-JSON (CSV text, PDF binary)
export async function apiGetRaw(path, queryParams = {}) {
  const headers = await getAuthHeaders();
  const endpoint = process.env.REACT_APP_API_ENDPOINT || '';
  const qs = new URLSearchParams(queryParams).toString();
  const url = `${endpoint}${path}${qs ? `?${qs}` : ''}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { headers, signal: controller.signal });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res;
  } finally {
    clearTimeout(timer);
  }
}

// Toast-free GET that still THROWS on failure — for queries that must
// distinguish "no data" from "fetch failed" without surfacing a global toast.
// (silentFetch resolves null on failure, which react-query would cache as
// successful empty data and never retry.)
export async function apiGetQuiet(path, queryParams = {}) {
  const headers = await getAuthHeaders();
  const endpoint = process.env.REACT_APP_API_ENDPOINT || '';
  const qs = new URLSearchParams(
    Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const url = `${endpoint}${path}${qs ? `${path.includes('?') ? '&' : '?'}${qs}` : ''}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { headers, signal: controller.signal });
    if (!res.ok) {
      const err = new Error(`Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// Toast-free JSON fetch for fire-and-forget calls (e.g. IAP receipt verification
// on every app launch — a stale receipt would otherwise surface an error toast
// each cold start). Returns parsed JSON on 2xx, null otherwise. Never throws.
export async function silentFetch(path, method = 'POST', body = null) {
  const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
  const endpoint = process.env.REACT_APP_API_ENDPOINT || '';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${endpoint}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// silentFetch that also reports the HTTP status, for callers that must tell a
// refusal (e.g. 409 "linked to a different account") from a failure (5xx,
// timeout, offline) — the IAP `verified` handler. Resolves
// { ok, status, data }: `data` is the parsed JSON body whatever the status
// (null when there is none), `status` is 0 when no answer arrived at all.
// Never throws, never toasts.
export async function silentFetchDetailed(path, method = 'POST', body = null) {
  const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
  const endpoint = process.env.REACT_APP_API_ENDPOINT || '';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${endpoint}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  } finally {
    clearTimeout(timer);
  }
}
